import { formatDayRu, formatMonthRu, nameGenitive, type DayKey, type MonthKey, type SphereId } from '@superposition/domain';

/**
 * русские тексты приложения: строчными, как в остальном интерфейсе.
 * строка с параметрами — функция с именованными параметрами.
 */
export const ru = {
  common: {
    /** название приложения: шапка, экраны входа, шторка плеера */
    brand: 'суперпозиция',
    /** заголовок вкладки, document.title */
    documentTitle: 'суперпозиция',
    close: 'закрыть',
    cancel: 'отмена',
    save: 'сохранить',
    saving: 'сохраняю…',
    saved: 'сохранено',
    /** день и месяц для показа */
    formatDay: (day: DayKey): string => formatDayRu(day),
    formatMonth: (month: MonthKey): string => formatMonthRu(month),
  },

  app: {
    nav: {
      wheel: 'колесо',
      matrix: 'матрица',
      obs: 'наблюдатель',
      lake: 'озеро',
      method: 'метод',
    },
    settingsAria: 'настройки',
    checkingSession: 'проверяю telegram-сессию…',
    noTelegram: 'это мини-приложение telegram. открой его внутри telegram — из меню бота @superposition_kate_bot.',
    noTelegramBrowser: 'в обычном браузере вход невозможен: подтвердить, что это ты, можно только через telegram.',
    accessExpiredTitle: 'срок доступа закончился',
    accessInviteTitle: 'доступ по приглашению',
    accessExpiredText: 'твои записи сохранены и вернутся, как только доступ продлят.',
    accessInviteText: 'суперпозиция - закрытое приложение.',
    accessInBot: 'доступ оформляется в боте.',
    renewAccess: 'продлить доступ',
    getAccess: 'получить доступ',
    /** handle — ник без @ или null, тогда пишем кате */
    writeTo: (p: { handle: string | null }): string =>
      `напиши ${p.handle ? `@${p.handle}` : 'кате'} и пришли этот номер:`,
    telegramIdAria: 'твой telegram id',
    writeKate: 'написать кате',
    accessRetry: 'доступ уже открыли — войти',
    authFailedTitle: 'не удалось войти',
    tryAgain: 'попробовать снова',
  },

  save: {
    pending: 'есть несохранённые изменения',
    retry: 'повторить',
    draft: 'черновик',
    confirmReload:
      'загрузить серверную версию? локальные несохранённые правки будут заменены. сначала сохрани черновик, если они нужны.',
    reloadFailed: 'не удалось загрузить данные. черновик сохранён в открытом окне — попробуй ещё раз.',
    takeServer: 'взять серверное',
    draftTitle: 'несохранённый черновик',
    draftText:
      'сервер не принял изменения. скопируй этот текст или выгрузи файлом — данные не пропадут, пока окно открыто.',
    download: 'выгрузить файлом',
    conflict: 'данные изменились с другого устройства — сохрани черновик перед загрузкой серверной версии',
  },

  errors: {
    noSession: 'нет активной сессии',
    timeout: 'сервер не ответил вовремя',
    offline: 'нет связи с сервером',
    requestFailed: 'запрос не прошёл',
    unknown: 'что-то пошло не так',
    offlineUnsaved: 'нет связи с сервером. изменения не сохранены — скопируй или выгрузи черновик',
    timeoutUnsaved: 'сервер не ответил вовремя. изменения не сохранены',
    unauthorized: 'сессия telegram истекла. закрой и открой мини-приложение заново',
    noAccess: 'срок доступа закончился. записи сохранены — после продления всё вернётся',
    conflict: 'данные изменились с другого устройства',
    tooLarge: 'файл больше 5 MiB — он не применён и не обрезан',
    /** requestId — первые символы номера запроса, если сервер его прислал */
    server: (p: { requestId: string | null }): string =>
      `сервис не справился${p.requestId ? ` (запрос ${p.requestId})` : ''}. изменения не сохранены`,
  },

  settings: {
    title: 'настройки',
    name: 'имя',
    namePlaceholder: 'имя',
    genderLabel: 'как к тебе обращаться',
    genderM: 'в мужском роде',
    genderF: 'в женском роде',
    genderN: 'без разницы',
    remindersOn: 'каждый день спрашивать в боте, как прошел день',
    remindAt: 'во сколько спрашивать',
    language: 'язык',
    /** название каждого языка — на нем самом */
    languageRu: 'русский',
    languageEn: 'english',
  },

  wheel: {
    title: 'колесо баланса',
    /** nameGen — имя в родительном падеже */
    // имя в родительном падеже: «колесо баланса Кати»
    titleOf: (p: { name: string; gender: 'm' | 'f' | 'n' | null }): string => `колесо баланса ${nameGenitive(p.name, p.gender)}`,
    aria: 'колесо восьми сфер',
    outOf10: (p: { value: number }): string => `${p.value} из 10`,
    vertexAria: (p: { sphere: string; value: number }): string => `${p.sphere}: ${p.value} из 10`,
    balanceIndex: 'индекс баланса',
    inner: 'внутренние',
    outer: 'внешние',
    dayFixed: 'день зафиксирован ✓ — обновить',
    fixDay: 'зафиксировать день',
    note: 'индекс баланса — авторская эвристика по твоим же оценкам. это не измерение когерентности и не прогноз дохода.',
  },

  matrix: {
    title: 'матрица',
    /** подсказки шагов при добавлении навыка */
    suggest: {
      sostoyanie: ['медитация', '10 минут тишины', 'дневник состояния', 'прогулка без телефона'],
      zdorovie: ['вода 2л', 'тренировка', 'сон до 23:00', '10 000 шагов'],
      otnosheniya: ['звонок близкому', 'время без телефона', 'написать другу'],
      prostranstvo: ['разобрать поверхность', 'навести порядок 15 мин', 'убрать рабочее место'],
      dengi: ['свести цифры дня', 'один денежный шаг', 'выставить счёт'],
      produkt: ['час над продуктом', 'один готовый кусок', 'показать результат'],
      vliyanie: ['один пост', 'публичная мысль', 'выйти в эфир'],
      realizatsiya: ['довести до «готово»', 'закрыть задачу', 'убрать незавершёнку'],
    } as Record<SphereId, string[]>,
    goalAria: (p: { skill: string; done: number; goal: number | null; lost: boolean }): string =>
      `${p.skill}: ${p.done} за месяц${p.goal ? `, цель ${p.goal}${p.lost ? ', уже не успеть' : ''}` : ', цель не задана'}`,
    today: 'сегодня',
    prevMonth: 'предыдущий месяц',
    nextMonth: 'следующий месяц',
    goalHeadTotal: 'итог',
    goalHeadGoal: 'цель',
    renameSphereAria: (p: { sphere: string }): string => `переименовать сферу ${p.sphere}`,
    addSkillAria: (p: { sphere: string }): string => `добавить навык в сферу ${p.sphere}`,
    addSkillEmpty: '+ добавить навык',
    renameSkillAria: (p: { skill: string }): string => `переименовать навык ${p.skill}`,
    deleteSkillAria: (p: { skill: string }): string => `удалить навык ${p.skill}`,
    skillsPerDay: 'навыков за день',
    balancePerDay: 'индекс баланса, %',
    note: 'индекс баланса берется из колеса в тот день, когда ты фиксируешь день.',
    newSkill: (p: { sphere: string }): string => `новый навык · ${p.sphere}`,
    skillNameLabel: 'название навыка',
    skillPlaceholder: 'свой шаг…',
    add: 'добавить',
    goalTitle: (p: { skill: string }): string => `цель на месяц: ${p.skill}`,
    goalQuestion: 'сколько раз в месяц ты хочешь это делать?',
    goalInputLabel: 'раз в месяц',
    goalNote: 'опирайся на прошлый месяц: сохрани навык или поменяй, если он не ведет к цели.',
    removeGoal: 'убрать цель',
    renameSkill: 'переименовать навык',
    renameSphere: 'переименовать сферу',
    newNameLabel: 'новое название',
    renameSkillNote: 'отметки навыка сохранятся.',
    /** standard — стандартное название сферы */
    renameSphereNote: (p: { standard: string }): string =>
      `оценки, навыки и связи с книгой сохранятся. стандартное название — «${p.standard}».`,
    restoreName: (p: { standard: string }): string => `вернуть «${p.standard}»`,
    deleteSkill: 'удалить навык',
    deleteTitle: (p: { skill: string }): string => `удалить навык «${p.skill}»?`,
    deleteText: (p: { marks: number }): string =>
      `вместе с навыком исчезнут его отметки: ${p.marks}. вернуть их будет нельзя.`,
    delete: 'удалить',
  },

  observer: {
    title: 'наблюдатель',
    looking: 'наблюдаю…',
    lookLine1: 'время',
    lookLine2: 'наблюдать',
    hint: 'нажми на циферблат',
    persona: 'сегодня я наблюдаю тебя как',
    oneStep: 'один шаг',
    whyInBook: 'почему так — в книге:',
    lakePractice: (p: { sphere: string }): string => `практика в озере: ${p.sphere} →`,
    emptyLine1: 'наблюдатель смотрит в матрицу и в колесо и схлопывает суперпозицию в один честный вывод.',
    emptyLine2: 'нажми на циферблат, когда будет готовность услышать.',
    past: (p: { count: number }): string => `прошлые наблюдения — ${p.count}`,
    showMore: 'показать ещё',
    note: 'наблюдатель работает по прозрачным правилам на твоих же отметках и оценках. он не обращается к сети и ничего не отправляет наружу.',
  },

  reports: {
    title: 'отчеты за месяц',
    verdict: {
      keep: 'оставить',
      replace: 'заменить навыки',
      neglected: 'без внимания',
      grows: 'растет сама',
      steady: 'ровно',
      unknown: '—',
    },
    month: (p: { n: number }): string => `${p.n}-й месяц`,
    period: (p: { from: DayKey; to: DayKey; filled: number; days: number }): string =>
      `${formatDayRu(p.from)} — ${formatDayRu(p.to)} · ${p.filled} из ${p.days} дней`,
    colSphere: 'сфера',
    colSkills: 'навыки',
    colValue: 'оценка',
    colVerdict: 'вывод',
    note: (p: { spheres: number }): string =>
      `навыки — доля единиц от возможных за месяц. оценка — удовлетворенность сферой от первой фиксации дня к последней. ${p.spheres} сфер, весь период.`,
  },

  lake: {
    title: 'сказочное озеро',
    intro: 'выбери сферу, которую заряжаешь, и опустись под воду. у сфер с записью звучит медитация, у остальных — тон для погружения.',
    intentionAria: 'намерение',
    recorded: (p: { minutes: number }): string => `запись · ${p.minutes} мин`,
    soon: 'скоро',
    durationAria: 'длительность',
    minutes: (p: { minutes: number }): string => `${p.minutes} мин`,
    enter: 'войти в озеро',
    practiceNote: (p: { title: string; minutes: number }): string =>
      `медитация «${p.title}», ${p.minutes} мин. лучше в наушниках, с закрытыми глазами. запись играет и при погасшем экране.`,
    soundOff: 'звук выключен',
    soundOn: 'звук включён',
    toneNote:
      'медитация для этой сферы скоро появится. пока — погружение под тон: лучше в наушниках, два тона 180 и 188 гц, разница 8 гц. дыхание и таймер работают и без звука.',
    weekly: 'новые практики каждую неделю',
    /** заголовок записи на экране блокировки */
    mediaTitle: (p: { title: string }): string => `сказочное озеро: ${p.title}`,
    // название записи как в книге
    practiceTitle: (p: { title: string }): string => p.title,
    pause: 'пауза',
    meditation: (p: { title: string }): string => `медитация: ${p.title}`,
    intention: (p: { sphere: string }): string => `намерение: ${p.sphere}`,
    resume: 'продолжить практику',
    recordFailed: 'запись не запустилась. нажми «продолжить» еще раз или проверь связь.',
    leave: 'выйти на берег',
    stopSound: 'выключить звук',
    startingSound: 'включаю звук…',
    startSound: 'включить звук',
    soundFailed: (p: { message: string }): string =>
      `звук не запустился: ${p.message}. практика продолжается — таймер и дыхание работают.`,
    noStereo: 'стерео-панорама недоступна на этом устройстве — тоны звучат вместе, это не бинауральный режим.',
    breath: {
      in: 'вдох',
      out: 'выдох',
    },
    /** причины, по которым тон не запустился */
    sound: {
      superseded: 'запуск отменён более новым',
      unavailable: 'звук недоступен на этом устройстве',
      noWebAudio: 'web audio не поддерживается этим браузером',
    },
  },

  method: {
    title: 'метод',
    /** заголовок записи на экране блокировки */
    mediaTitle: (p: { title: string }): string => `медитация: ${p.title}`,
    pauseAria: (p: { title: string }): string => `пауза: ${p.title}`,
    listenAria: (p: { title: string }): string => `слушать медитацию: ${p.title}`,
    practice: (p: { title: string }): string => `медитация · ${p.title}`,
    seekAria: (p: { title: string }): string => `перемотка: ${p.title}`,
    playFailed: 'не получилось запустить, нажми еще раз',
    allChapters: '← все главы',
    chapter: 'глава',
    openingChapter: 'открываю главу…',
    intro: 'книга, из которой выросли колесо, матрица и наблюдатель. читается по главам, с любого места.',
    loadingIndex: 'загружаю оглавление…',
  },
};
